import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import ReactMarkdown from 'react-markdown';
import { 
  fetchTermsOfService, 
  fetchPrivacyPolicy, 
  recordUserAcceptance, 
  LegalDocument 
} from '@/src/services/legalService';

interface Props {
  isOpen: boolean;
  userId: string;
  onAcceptanceComplete: () => void;
  requiredTermsVersion: string;
  requiredPrivacyVersion: string;
}

const LegalBlockingModal: React.FC<Props> = ({ 
  isOpen, 
  userId, 
  onAcceptanceComplete,
  requiredTermsVersion,
  requiredPrivacyVersion
}) => {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');
  const [termsDoc, setTermsDoc] = useState<LegalDocument | null>(null);
  const [privacyDoc, setPrivacyDoc] = useState<LegalDocument | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    
    let active = true;
    Promise.all([fetchTermsOfService(), fetchPrivacyPolicy()]).then(([t, p]) => {
      if (active) {
        setTermsDoc(t);
        setPrivacyDoc(p);
        setLoading(false);
      }
    });

    return () => { active = false; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAcceptAll = async () => {
    if (!termsAccepted || !privacyAccepted) return;
    setSubmitting(true);
    try {
      await Promise.all([
        recordUserAcceptance(userId, 'terms', requiredTermsVersion),
        recordUserAcceptance(userId, 'privacy', requiredPrivacyVersion)
      ]);
      setSubmitting(false);
      onAcceptanceComplete();
    } catch (err) {
      console.error('Error logging legal acceptances:', err);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/80 flex flex-col overflow-hidden items-center justify-center p-4">
      <div className="w-full max-w-4xl h-full max-h-[90vh] flex flex-col border border-white/[0.06] bg-[#3a3430] rounded-xl overflow-hidden shadow-2xl relative text-white">
        
        {/* Banner */}
        <header className="px-8 py-6 border-b border-white/[0.06] bg-[#322D29] shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Protocol Version Alignment</span>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight font-heading text-white leading-tight mt-0.5">
              Action Required: Protocol Acknowledgment
            </h2>
            <p className="text-xs text-white/60 mt-1">
              eNvesta AI has updated its standard governing guidelines and privacy directives. Please review and confirm agreement to proceed.
            </p>
          </div>
          
          <div className="flex items-center gap-1.5 bg-[#3a3430] p-1 border border-white/[0.06] rounded-lg shrink-0">
            <button
              onClick={() => setActiveTab('terms')}
              className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider rounded transition-all cursor-pointer ${
                activeTab === 'terms' ? 'bg-[#F26522] text-white' : 'text-white/50 hover:text-white'
              }`}
            >
              Terms of Service
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider rounded transition-all cursor-pointer ${
                activeTab === 'privacy' ? 'bg-[#F26522] text-white' : 'text-white/50 hover:text-white'
              }`}
            >
              Privacy Policy
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8 bg-[#3a3430] text-white max-h-[55vh] border-b border-white/[0.06] custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-10 h-10 border-2 border-white/20 border-t-[#F26522] rounded-full animate-spin mb-3"></div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Loading document manifests...</span>
            </div>
          ) : activeTab === 'terms' ? (
            <div className="markdown-body prose prose-invert max-w-none animate-in fade-in duration-200">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-white/[0.06]">
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Version: {termsDoc?.version}</span>
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Effective: {termsDoc?.effectiveDate}</span>
              </div>
              <ReactMarkdown>{termsDoc?.content || ''}</ReactMarkdown>
            </div>
          ) : (
            <div className="markdown-body prose prose-invert max-w-none animate-in fade-in duration-200">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-white/[0.06]">
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Version: {privacyDoc?.version}</span>
                <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest">Effective: {privacyDoc?.effectiveDate}</span>
              </div>
              <ReactMarkdown>{privacyDoc?.content || ''}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Acceptance Panel */}
        <footer className="p-8 bg-[#322D29] border-t border-white/[0.06] shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 text-[#F26522] focus:ring-[#F26522] accent-[#F26522] cursor-pointer"
              />
              <span className="text-[11px] font-medium text-white/80 uppercase tracking-wider">
                I accept the updated <span className="text-[#F26522] hover:underline font-bold">Terms of Service</span> (v{requiredTermsVersion})
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 text-[#F26522] focus:ring-[#F26522] accent-[#F26522] cursor-pointer"
              />
              <span className="text-[11px] font-medium text-white/80 uppercase tracking-wider">
                I accept the updated <span className="text-[#F26522] hover:underline font-bold">Privacy Policy</span> (v{requiredPrivacyVersion})
              </span>
            </label>
          </div>

          <button
            onClick={handleAcceptAll}
            disabled={!termsAccepted || !privacyAccepted || submitting}
            className="px-8 py-4 bg-[#F26522] text-white font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-[#F26522]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0 flex items-center gap-3 cursor-pointer"
          >
            {submitting ? (
              <>
                <i className="fas fa-circle-notch fa-spin"></i>
                Synchronizing...
              </>
            ) : (
              <>
                <i className="fas fa-check-double"></i>
                Acknowledge and Terminate Block
              </>
            )}
          </button>
        </footer>

      </div>
    </div>
  );
};

export default LegalBlockingModal;
